import { ArrowBack, Clear, Edit, LocalParking } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { AddVINDialog, ExplanationAccordion, GreenText, LabelField } from 'components'
import { Chip } from 'components/chips'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { VehicleDto, VehicleParkingSpaceDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ParkingSpaceEditDialog from './ParkingSpaceEditDialog'

interface VehicleDetailsProps {
  vehicle: VehicleDto
  setVehicle: React.Dispatch<React.SetStateAction<VehicleDto>>
}

export default function VehicleDetailsCard({ vehicle, setVehicle }: VehicleDetailsProps) {
  const [isCreatingVehicle, setIsCreatingVehicle] = useState<boolean>(false)
  const [isGettingVehicleParkingSpace, setIsGettingVehicleParkingSpace] = useState<boolean>(true)
  const [isUpdatingVehicleParkingSpace, setIsUpdatingVehicleParkingSpace] = useState<boolean>(false)
  const [vehicleParkingSpace, setVehicleParkingSpace] = useState<VehicleParkingSpaceDto>(new VehicleParkingSpaceDto())
  const [ParkingSpaceEditDialogOpen, setParkingSpaceEditDialogOpen] = useState<boolean>(false)
  const [selectAddVINDialogOpen, setAddVINDialogOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const { user, permissions } = useAuth()

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingVehicleParkingSpace(true)
      controllers
        .getVehicleParkingSpace(vehicle.vehicleGuid)
        .then(response => {
          setVehicleParkingSpace(response.value)
        })
        .finally(() => {
          setIsGettingVehicleParkingSpace(false)
        })
    }
  }, [vehicle])

  const handleCancelImport = () => {
    if (window.confirm('Are you sure you would like to cancel this import? This action cannot be undone.')) {
      controllers.cancelImport(vehicle.vehicleGuid!).then(response => {
        setVehicle(response.value)
        enqueueSnackbar('Import Cancelled Successfully!', { variant: 'success' })
      })
    }
  }

  return (
    <>
      <AddVINDialog
        initialValues={vehicle}
        isLoading={isCreatingVehicle}
        onClose={() => {
          setAddVINDialogOpen(false)
        }}
        onSave={values => {
          setIsCreatingVehicle(true)

          controllers
            .updateVehicle(values)
            .then(response => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              const vehicle = response.value
              setVehicle(vehicle)

              // if (vehicle?.vehicleGuid) {
              //   const activityLogEntry: VehicleActivityLogDto = {
              //     createdDateTime: new Date().toISOString(),
              //     createdBy: user!,
              //     message: `Vehicle Details updated.`
              //   }
              //   controllers.createActivityLog(activityLogEntry, vehicle.vehicleGuid)
              // }

              setAddVINDialogOpen(false)
            })
            .finally(() => {
              setIsCreatingVehicle(false)
            })
        }}
        open={selectAddVINDialogOpen}
      />

      <ParkingSpaceEditDialog
        initialValues={vehicleParkingSpace}
        isLoading={isUpdatingVehicleParkingSpace}
        onClose={() => {
          setParkingSpaceEditDialogOpen(false)
        }}
        onSave={values => {
          setIsUpdatingVehicleParkingSpace(true)
          controllers
            .updateVehicleParkingSpace(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Parking Space Saved Successfully!', { variant: 'success' })
              const parkingSpace = response.value
              setVehicleParkingSpace(parkingSpace)

              // if (vehicle?.vehicleGuid) {
              //   const activityLogEntry: VehicleActivityLogDto = {
              //     createdDateTime: new Date().toISOString(),
              //     createdBy: user!,
              //     message: `Vehicle Parking Space updated.`
              //   }
              //   controllers.createActivityLog(activityLogEntry, vehicle.vehicleGuid)
              // }
              setParkingSpaceEditDialogOpen(false)
            })
            .finally(() => {
              setIsUpdatingVehicleParkingSpace(false)
            })
        }}
        open={ParkingSpaceEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container alignItems='center' spacing={0}>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5' sx={{ pr: 1 }}>
                    Stock #{vehicle.importerStockNumber ?? '-'}
                  </Typography>
                </Grid>

                {vehicle.isCancelled && (
                  <Grid item>
                    <Chip variant='red'>CANCELLED</Chip>
                  </Grid>
                )}

                {vehicle.isArchived && (
                  <Grid item>
                    <Chip variant='red'>ARCHIVED</Chip>
                  </Grid>
                )}

                {vehicle.isLocked && (
                  <Grid item>
                    <Chip variant='red'>VEHICLE LOCKED</Chip>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Button
                    onClick={() => {
                      navigate('/to-do-list')
                    }}
                    color='primary'
                    startIcon={<ArrowBack />}
                    variant='text'
                  >
                    BACK
                  </Button>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>
                  <Button
                    color='error'
                    disabled={vehicle.isCancelled || vehicle.isLocked || !permissions.CANCEL_IMPORT_CAPABILITY}
                    endIcon={<Clear />}
                    onClick={handleCancelImport}
                    variant='text'
                  >
                    CANCEL IMPORT
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    onClick={() => {
                      setAddVINDialogOpen(true)
                    }}
                    endIcon={<Edit />}
                    variant='text'
                  >
                    EDIT VIN
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    disabled={isGettingVehicleParkingSpace}
                    onClick={() => {
                      setParkingSpaceEditDialogOpen(true)
                    }}
                    endIcon={<LocalParking />}
                    variant='text'
                  >
                    EDIT PARKING
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField label='VIN' value={vehicle.vin} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField label='Year' value={vehicle.year} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField label='Make' value={vehicle.make} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField label='Model' value={vehicle.model} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField label='Exporter' value={vehicle.exporterCompany?.name} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <LabelField isLoading={isGettingVehicleParkingSpace} label='Parking Space' value={vehicleParkingSpace.parkingSpace} />
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Stock Number:**

Importer Stock #: The Importer Stock# is a sequential number beginning with 20000 followed by the 3 character “Buyer Code” (e.g., 20001-ABC), else the 3 character “Exporter Code” if no Buyer Company is selected for the vehicle under Vehicle Details – Exporter. The value is assigned and displayed when the vehicle is locked for the first time. If the vehicle becomes unlocked, the Importer Stock# remains (the Importer Stock# cannot be changed or removed from a vehicle once assigned). If the vehicle has never been locked, the stock number will display as “-”.

***Archived Chip:***

The "Archived" chip/status is applied one day after the “QuickBooks Invoice #” is populated for the vehicle under Vehicle Details – Invoicing (tab).

The "Archived" chip/status is automatically applied X days after the vehicle import is canceled, where X is the number of days set in the Auto Archive After Import Cancellation Date card under Settings in VTS2

***BACK Button:***

Clicking the BACK button will take the user to the previous To-Do List screen.

**CANCEL IMPORT Button:**

Clicking the CANCEL IMPORT button will stop the vehicle import process. This action will:
   - Add a "Canceled" tag to the vehicle record, visible in the To-Do List.
   - Append a label to the vehicle record that reads: "Canceled on {date} by {user}."

The CANCEL IMPORT button is disabled under the following conditions:
   - The vehicle is locked.
   - The vehicle is associated with a PAPS.
   - The vehicle has at least one Check-In Photo uploaded.

The button is only accessible to users with the VTS2 Access “Cancel Import Capability”.

**EDIT VIN Button:**

Clicking the EDIT VIN button opens the Add/Edit VIN dialog.

**EDIT PARKING Button:**

Clicking the EDIT PARKING button opens the Edit Parking Space dialog.

**VIN:**

Displays the vehicle's VIN as entered in the Add/Edit VIN dialog.

**Year:**

Displays the vehicle's year as entered in the Add/Edit VIN dialog.

**Make:**

Displays the vehicle's make as entered in the Add/Edit VIN dialog.

**Model:**

Displays the vehicle's model as entered in the Add/Edit VIN dialog.

**Exporter:**

Displays the exporter associated with the vehicle, as selected in the Add/Edit VIN dialog.

**Parking Space:**

Displays the vehicle's parking space location as entered in the Edit Parking dialog.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
